/**
 * useQuickArmHandlers — iter-244
 *
 * Bundles all 13 Quick Arm / routing / community-preset handlers that
 * used to live inline inside `ELPMStudioDAW.jsx` (iter-206 → iter-208
 * features). Extracting them shrinks the parent file by ~235 lines and
 * gives the handlers a stable, testable surface.
 *
 * Behaviour is byte-identical to the inline versions — this is a pure
 * cut-and-paste with no logic changes.
 *
 * Inputs are passed via a single options bag so call sites stay
 * readable. Every handler is a stable closure-bound function (no
 * useCallback wrapper — React 19 batches state updates and the parent
 * passes them down to memoised children that already memoise on
 * primitive props, not function identity).
 */
import { toast } from "sonner";
import { padCh, TOTAL_CHANNELS } from "@/components/daw/dawHelpers";
import { loadCustomPresets, saveCustomPreset, removeCustomPreset } from "@/components/daw/quickArmPresets";

export default function useQuickArmHandlers({
  armedChannels,
  setArmedChannels,
  sourceMap,
  setSourceMap,
  visibleChannels,
  inputChannelCount,
  setCustomArmPresets,
  setRoutingMatrixOpen,
  arTpl,
  authUser,
}) {
  const handleToggleArm = (channelIdx) => {
    setArmedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channelIdx)) next.delete(channelIdx);
      else next.add(channelIdx);
      return next;
    });
  };

  const handleArmAll = () => {
    setArmedChannels(new Set(Array.from({ length: visibleChannels }, (_, i) => i)));
    toast.message(`Armed ${visibleChannels} channels`);
  };

  const handleDisarmAll = () => {
    setArmedChannels(new Set());
    toast.message("All channels disarmed");
  };

  // iter-206 — One-tap template that arms the first N channels, applies
  // round-robin input routing, AND pops the routing matrix open so the
  // user can confirm/tweak before pressing Record. Cuts the typical
  // "arm 4 → routing → balance" workflow from 3 clicks to 1.
  const handleQuickArmTemplate = (n, label) => {
    if (!n || n <= 0) return;
    const capped = Math.min(n, TOTAL_CHANNELS);
    const channelsToArm = Array.from({ length: capped }, (_, i) => i);
    const inputs = Math.max(1, inputChannelCount || 1);

    setArmedChannels(new Set(channelsToArm));
    setSourceMap((m) => {
      const next = { ...m };
      channelsToArm.forEach((ch, i) => { next[ch] = i % inputs; });
      return next;
    });
    setRoutingMatrixOpen(true);
    toast.success(`Armed ${capped} channels · ${label}`, {
      description: inputs === 1
        ? `Only 1 input available — all ${capped} channels mapped to Input 1. Plug in a multi-input interface to route them independently.`
        : `Round-robin routing pre-applied across ${inputs} inputs. Tweak in the routing matrix or press Record All Armed to start tracking.`,
      duration: 7000,
    });
  };

  // iter-207 — Save current armed-channels + sourceMap as a named preset.
  const handleSaveCustomArmPreset = () => {
    if (armedChannels.size === 0) {
      toast.message("Arm some channels first", {
        description: "Pick the channels you want to save as a personal template, then come back.",
      });
      return;
    }
    const defaultName = `${armedChannels.size}-ch setup`;
    const name = typeof window !== "undefined"
      ? window.prompt("Name this arm-routing preset:", defaultName)
      : defaultName;
    if (name === null) return; // user hit Cancel
    const channels = Array.from(armedChannels);
    const sm = {};
    channels.forEach((ch) => { sm[ch] = sourceMap[ch] ?? 0; });
    const entry = saveCustomPreset(name, channels, sm);
    setCustomArmPresets(loadCustomPresets());
    toast.success(`Saved "${entry.name}"`, {
      description: `${entry.channels.length} channel${entry.channels.length === 1 ? "" : "s"} · click the new pill to reapply later. Stored locally in your browser.`,
      duration: 6000,
    });
  };

  const handleApplyCustomArmPreset = (preset) => {
    if (!preset || !Array.isArray(preset.channels) || preset.channels.length === 0) {
      toast.error("Preset is empty", { description: "This preset has no channels saved." });
      return;
    }
    setArmedChannels(new Set(preset.channels));
    setSourceMap((m) => ({ ...m, ...(preset.sourceMap || {}) }));
    setRoutingMatrixOpen(true);
    toast.success(`Applied "${preset.name}"`, {
      description: `${preset.channels.length} channel${preset.channels.length === 1 ? "" : "s"} armed with your saved routing. Tweak in the routing matrix or press Record All Armed.`,
      duration: 6000,
    });
  };

  const handleRemoveCustomArmPreset = (id) => {
    removeCustomPreset(id);
    setCustomArmPresets(loadCustomPresets());
    toast.message("Preset deleted");
  };

  // iter-208 — Public marketplace for arm/routing templates.
  // Share button → publish a saved custom preset to the community.
  const handleShareCustomArmPreset = async (preset) => {
    if (!authUser) {
      toast.error("Sign in to share presets", {
        description: "Public templates need an attributed creator. Open the user menu in the top-right to sign in with Google.",
      });
      return;
    }
    if (!preset || !Array.isArray(preset.channels) || preset.channels.length === 0) {
      toast.error("Preset is empty", { description: "Save a non-empty arm layout before sharing." });
      return;
    }
    const allowedCats = ["podcast", "drums", "band", "dj", "vocals", "instruments", "other"];
    const catRaw = typeof window !== "undefined"
      ? window.prompt(
          `Category for "${preset.name}"?  Pick one:\n${allowedCats.join(" · ")}`,
          "other",
        )
      : "other";
    if (catRaw === null) return; // user hit Cancel
    const category = allowedCats.includes((catRaw || "").trim().toLowerCase())
      ? catRaw.trim().toLowerCase()
      : "other";
    const description = typeof window !== "undefined"
      ? (window.prompt("Optional one-line description (or leave blank):", "") || "")
      : "";
    try {
      const entry = await arTpl.publish({
        title: preset.name,
        description,
        category,
        channels: preset.channels,
        sourceMap: preset.sourceMap || {},
      });
      toast.success(`Shared "${entry.title}"`, {
        description: `Now live in Community · ${entry.channels?.length || 0} channels · ${category} category. Use the Trash icon on hover to remove later.`,
        duration: 7000,
      });
    } catch (e) {
      toast.error("Could not publish preset", {
        description: e?.message || "Try again in a moment.",
        duration: 8000,
      });
    }
  };

  // Apply a community preset → arm channels + restore sourceMap + bump usage counter.
  const handleApplyCommunityArmPreset = async (template) => {
    if (!template || !Array.isArray(template.channels) || template.channels.length === 0) {
      toast.error("Community preset is empty");
      return;
    }
    const sm = {};
    for (const [k, v] of Object.entries(template.source_map || {})) {
      sm[Number(k)] = Number(v);
    }
    setArmedChannels(new Set(template.channels));
    setSourceMap((m) => ({ ...m, ...sm }));
    setRoutingMatrixOpen(true);
    arTpl.markUsed(template.id);
    toast.success(`Applied "${template.title}"`, {
      description: `${template.channels.length} channel${template.channels.length === 1 ? "" : "s"} armed · ${template.creator_name}'s routing intact.`,
      duration: 6000,
    });
  };

  const handleToggleCommunityLike = async (id) => {
    if (!authUser) {
      toast.error("Sign in to like community presets", {
        description: "Open the user menu in the top-right to sign in with Google.",
      });
      return;
    }
    try {
      await arTpl.toggleLike(id);
    } catch (e) {
      toast.error("Could not toggle like", { description: e?.message });
    }
  };

  const handleDeleteCommunityPreset = async (id, title) => {
    if (typeof window !== "undefined") {
      const ok = window.confirm(`Delete community preset "${title || id}"? This cannot be undone.`);
      if (!ok) return;
    }
    try {
      await arTpl.remove(id);
      toast.message("Community preset deleted");
    } catch (e) {
      toast.error("Could not delete preset", { description: e?.message });
    }
  };

  // iter-204 — Auto-swap: if another armed channel already uses the new
  // `src`, swap their inputs. The dedupe pass in `startTrackingSession`
  // would silently drop duplicates, so a UI-level swap is the cleaner
  // contract — user always sees a 1:1 mapping while picking.
  const handleSourceChChange = (channelIdx, src) => {
    setSourceMap((m) => {
      const next = { ...m, [channelIdx]: src };
      const oldSrc = m[channelIdx] ?? 0;
      for (const otherCh of armedChannels) {
        if (otherCh === channelIdx) continue;
        const otherSrc = m[otherCh] ?? 0;
        if (otherSrc === src) {
          next[otherCh] = oldSrc;
          toast.message(`Swapped inputs · CH ${padCh(channelIdx)} ↔ CH ${padCh(otherCh)}`, {
            description: `CH ${padCh(channelIdx)} → Input ${src + 1} · CH ${padCh(otherCh)} → Input ${oldSrc + 1}`,
            duration: 4000,
          });
          break; // at most one match — earlier swaps prevented others
        }
      }
      return next;
    });
  };

  // iter-205 — One-click round-robin distribution across N inputs.
  const handleAutoBalanceRouting = () => {
    const inputs = Math.max(1, inputChannelCount || 1);
    const armedList = Array.from(armedChannels).sort((a, b) => a - b);
    if (armedList.length === 0) return;
    setSourceMap((m) => {
      const next = { ...m };
      armedList.forEach((ch, i) => { next[ch] = i % inputs; });
      return next;
    });
    if (inputs === 1) {
      toast.message("All armed channels mapped to Input 1", {
        description: "Only one input is available on this interface. Switch to a multi-input audio device to enable true round-robin distribution.",
        duration: 6000,
      });
    } else {
      toast.success(`Auto-balanced ${armedList.length} channels across ${inputs} inputs`, {
        description: armedList
          .map((ch, i) => `CH ${padCh(ch)} → Input ${(i % inputs) + 1}`)
          .join(" · "),
        duration: 8000,
      });
    }
  };

  return {
    handleToggleArm,
    handleArmAll,
    handleDisarmAll,
    handleQuickArmTemplate,
    handleSaveCustomArmPreset,
    handleApplyCustomArmPreset,
    handleRemoveCustomArmPreset,
    handleShareCustomArmPreset,
    handleApplyCommunityArmPreset,
    handleToggleCommunityLike,
    handleDeleteCommunityPreset,
    handleSourceChChange,
    handleAutoBalanceRouting,
  };
}
